import { AgGridModule } from "@ag-grid-community/angular";
import { ColDef, GridApi, GridOptions, IRowNode } from "@ag-grid-community/core";
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from "@angular/core";
import { IButtonDirective, ITooltipDirective } from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import {
  BulkUploadBuckets,
  BulkUploadRowPreview,
  SupportedBulkUploadEntityModel,
} from "@m19-board/services/bulk-upload/bulk-upload-context.service";
import { BulkUploadGridUtilsService } from "@m19-board/services/bulk-upload/bulk-upload-grid-utils.service";
import {
  BulkUploadPreviewStatus,
  BulkUploadTypeIconComponent,
} from "../bulk-upload-grid-cells/bulk-upload-type-icon.component";
import { BulkUploadTypeFilterComponent } from "../bulk-upload-type-filter/bulk-upload-type-filter.component";

@Component({
  selector: "app-bulk-upload-preview",
  imports: [
    AgGridModule,
    BulkUploadTypeIconComponent,
    IButtonDirective,
    TranslocoDirective,
    ITooltipDirective,
    BulkUploadTypeFilterComponent,
  ],
  templateUrl: "./bulk-upload-preview.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkUploadPreviewComponent<T extends SupportedBulkUploadEntityModel> {
  public readonly previewBuckets = input.required<BulkUploadBuckets<T>>();
  public readonly columnDefs = input.required<ColDef<BulkUploadRowPreview<T>>[]>();
  private readonly bulkUploadGridUtilsService = inject(BulkUploadGridUtilsService);

  public readonly back = output<void>();
  public readonly upload = output<void>();

  BulkUploadPreviewStatus = BulkUploadPreviewStatus;
  private gridApi: GridApi<BulkUploadRowPreview<T>> | undefined;

  public selectedTypesFilter = signal<BulkUploadPreviewStatus[]>([]);

  public readonly creations = computed(() => {
    return this.previewBuckets().filter((row) => row.status === BulkUploadPreviewStatus.CREATION);
  });

  public readonly updates = computed(() => {
    return this.previewBuckets().filter((row) => row.status === BulkUploadPreviewStatus.UPDATE);
  });

  public readonly errors = computed(() => {
    return this.previewBuckets().filter((row) => row.status === BulkUploadPreviewStatus.VALUE_ERROR);
  });

  public readonly unchanged = computed(() => {
    return this.previewBuckets().filter((row) => row.status === BulkUploadPreviewStatus.UNCHANGED);
  });

  canUpload = computed(() => {
    return this.creations().length > 0 || this.updates().length > 0;
  });

  private sortedPreviewBuckets = computed(() => {
    return [...this.errors(), ...this.creations(), ...this.updates(), ...this.unchanged()];
  });

  readonly gridOptions = computed<GridOptions<BulkUploadRowPreview<T>>>(() => {
    const gridOptions = this.bulkUploadGridUtilsService.getGridOptions(this.sortedPreviewBuckets(), this.columnDefs());
    return {
      ...gridOptions,
      isExternalFilterPresent: this.hasTypeFilter,
      doesExternalFilterPass: this.doesTypeFilterPass,
      onGridReady: (params) => {
        gridOptions.onGridReady?.(params);
        this.gridApi = params.api;
      },
    };
  });

  hasTypeFilter = (): boolean => {
    return this.selectedTypesFilter().length > 0;
  };

  doesTypeFilterPass = (params: IRowNode<BulkUploadRowPreview<T>>): boolean => {
    return this.selectedTypesFilter().includes(params.data!.status);
  };

  typeFilterChanged(newValue: BulkUploadPreviewStatus[]): void {
    this.selectedTypesFilter.set(newValue);
    this.gridApi?.onFilterChanged();
  }
}
